// src/components/cards/TodaysKennelsCard.tsx - Display available kennels for selected date
import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, CardContent } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { kennelsAPI, KennelGrouped } from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';

interface TodaysKennelsCardProps {
  date: string;
}

// Interface for flattened kennel data
interface FlattenedKennel {
  id: number;
  kennelNumber: number;
  size: string;
  kennelSideId: number;
  sideName: string;
}

const CardContainer = styled(Box)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark' ? theme.palette.grey[900] : '#ffffff',
  padding: '2rem',
}));

const EmptyState = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: '2rem',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  color: theme.palette.text.secondary,
  gap: '1.5rem',
}));

const KennelGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
  gap: '1.5rem',
  marginTop: '1rem',
});

const KennelItemCard = styled(Card)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.palette.grey[800]
      : theme.palette.background.paper,
  borderRadius: '8px',
  border: `2px solid ${theme.palette.divider}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    boxShadow: theme.shadows[4],
    transform: 'translateY(-2px)',
  },
}));

const KennelName = styled(Typography)(({ theme }) => ({
  fontSize: '1.2rem',
  fontWeight: 700,
  marginBottom: '0.5rem',
  color: theme.palette.primary.main,
}));

const KennelInfo = styled(Typography)(({ theme }) => ({
  fontSize: '0.95rem',
  color: theme.palette.text.secondary,
  marginBottom: '0.25rem',
}));

const StatusBadge = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'available',
})<{ available: boolean }>(({ theme, available }) => ({
  display: 'inline-block',
  padding: '0.5rem 1rem',
  borderRadius: '20px',
  fontSize: '0.85rem',
  fontWeight: 600,
  marginTop: '0.75rem',
  backgroundColor: available
    ? 'rgba(76, 175, 80, 0.2)'
    : 'rgba(244, 67, 54, 0.2)',
  color: available ? '#4CAF50' : '#F44336',
}));

const TodaysKennelsCard: React.FC<TodaysKennelsCardProps> = ({ date }) => {
  const theme = useTheme();
  const [kennelGroups, setKennelGroups] = useState<KennelGrouped[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load kennels information
  useEffect(() => {
    setLoading(true);
    setError(null);

    kennelsAPI
      .getGrouped()
      .then((kennelData) => {
        setKennelGroups(kennelData);
      })
      .catch((err) => {
        console.error('Failed to load kennels:', err);
        setError('Failed to load kennels information');
        setKennelGroups([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [date]); // Include date in dependencies for consistency

  // Flatten kennels from grouped structure
  const allKennels: FlattenedKennel[] = kennelGroups.flatMap((group) =>
    group.kennels.map((kennel) => ({
      ...kennel,
      sideName: group.kennelSide.name,
    }))
  );

  if (loading) {
    return (
      <CardContainer>
        <LoadingSpinner message="Loading kennels..." size={40} />
      </CardContainer>
    );
  }

  if (error) {
    return (
      <CardContainer>
        <EmptyState>
          <Typography variant="body1" color="error">
            {error}
          </Typography>
        </EmptyState>
      </CardContainer>
    );
  }

  if (allKennels.length === 0) {
    return (
      <CardContainer>
        <EmptyState>
          <Typography
            variant="h4"
            sx={{ fontSize: '2rem', color: `${theme.palette.primary.main}` }}
          >
            No Kennels
          </Typography>
          <Typography variant="body1" sx={{ fontSize: '1rem' }}>
            Unable to load kennel information
          </Typography>
        </EmptyState>
      </CardContainer>
    );
  }

  return (
    <CardContainer>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Kennel Status for {date}
      </Typography>
      <KennelGrid>
        {allKennels.map((kennel) => (
          <KennelItemCard key={kennel.id}>
            <CardContent>
              <KennelName>Kennel {kennel.kennelNumber}</KennelName>
              <KennelInfo>
                <strong>Side:</strong> {kennel.sideName}
              </KennelInfo>
              <KennelInfo>
                <strong>Size:</strong> {kennel.size || 'Standard'}
              </KennelInfo>
              <StatusBadge available={true}>Available</StatusBadge>
            </CardContent>
          </KennelItemCard>
        ))}
      </KennelGrid>
    </CardContainer>
  );
};

export default TodaysKennelsCard;
